import React, { useState } from "react";
import { Settings, User, Lock, Users, Eye, EyeOff, Plus, X, ToggleLeft, ToggleRight } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authAPI, usuariosAPI } from "../services/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

type Tab = "perfil" | "password" | "usuarios";

const ROL_LABELS: Record<string,string> = { jugador: "Jugador", padre: "Padre/Tutor" };

interface UsuarioForm { email: string; password: string; nombre: string; apellido: string; rol: string; }
const EMPTY_UFORM: UsuarioForm = { email:"", password:"", nombre:"", apellido:"", rol:"jugador" };

export default function SettingsPage() {
  const { user, refreshMe, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("perfil");

  // ── Perfil
  const [perfil, setPerfil] = useState({ nombre: user?.nombre||"", apellido: user?.apellido||"", email: user?.email||"" });

  const perfilMutation = useMutation({
    mutationFn: () => authAPI.updateMe(perfil),
    onSuccess: async () => { await refreshMe(); toast.success("Perfil actualizado"); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Error"),
  });

  // ── Contraseña
  const [pwd, setPwd] = useState({ password_actual:"", password_nuevo:"", confirmar:"" });
  const [showPwds, setShowPwds] = useState({ actual:false, nuevo:false });

  const pwdMutation = useMutation({
    mutationFn: () => authAPI.changePassword({ password_actual: pwd.password_actual, password_nuevo: pwd.password_nuevo }),
    onSuccess: () => { toast.success("Contraseña actualizada"); setPwd({ password_actual:"", password_nuevo:"", confirmar:"" }); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Contraseña actual incorrecta"),
  });

  function handlePwd(e: React.FormEvent) {
    e.preventDefault();
    if (pwd.password_nuevo !== pwd.confirmar) { toast.error("Las contraseñas no coinciden"); return; }
    if (pwd.password_nuevo.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    pwdMutation.mutate();
  }

  // ── Usuarios jugador/padre
  const { data: usuarios = [], isLoading: loadingU } = useQuery({
    queryKey: ["usuarios"],
    queryFn: () => usuariosAPI.listar().then(r => r.data),
    enabled: isAdmin(),
  });

  const [showUForm, setShowUForm] = useState(false);
  const [uForm, setUForm] = useState<UsuarioForm>(EMPTY_UFORM);
  const [showUPwd, setShowUPwd] = useState(false);

  const crearU = useMutation({
    mutationFn: (d: UsuarioForm) => usuariosAPI.crear(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["usuarios"] }); toast.success("Usuario creado"); setShowUForm(false); setUForm(EMPTY_UFORM); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Error al crear"),
  });

  const toggleU = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => usuariosAPI.actualizar(id, { is_active: activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["usuarios"] }),
    onError: () => toast.error("Error"),
  });

  const tabs: { id: Tab; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
    { id:"perfil",   label:"Mi perfil",       icon:<User size={15}/> },
    { id:"password", label:"Contraseña",       icon:<Lock size={15}/> },
    { id:"usuarios", label:"Accesos (jugadores/padres)", icon:<Users size={15}/>, adminOnly:true },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <Settings size={20} className="text-gray-500"/>
          <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-8">
          {tabs.filter(t => !t.adminOnly || isAdmin()).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}>
              {t.icon} <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Perfil */}
        {tab === "perfil" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-5">Información personal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Nombre *</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={perfil.nombre} onChange={e=>setPerfil(p=>({...p, nombre:e.target.value}))} required/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Apellido</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={perfil.apellido} onChange={e=>setPerfil(p=>({...p, apellido:e.target.value}))}/>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Email *</label>
                <input type="email" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={perfil.email} onChange={e=>setPerfil(p=>({...p, email:e.target.value}))} required/>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => perfilMutation.mutate()} disabled={perfilMutation.isPending}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 shadow-sm">
                {perfilMutation.isPending ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        )}

        {/* Contraseña */}
        {tab === "password" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-5">Cambiar contraseña</h2>
            <form onSubmit={handlePwd} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Contraseña actual *</label>
                <div className="relative">
                  <input type={showPwds.actual?"text":"password"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={pwd.password_actual} onChange={e=>setPwd(p=>({...p, password_actual:e.target.value}))} required/>
                  <button type="button" onClick={()=>setShowPwds(p=>({...p, actual:!p.actual}))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPwds.actual ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Nueva contraseña *</label>
                <div className="relative">
                  <input type={showPwds.nuevo?"text":"password"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={pwd.password_nuevo} onChange={e=>setPwd(p=>({...p, password_nuevo:e.target.value}))} required/>
                  <button type="button" onClick={()=>setShowPwds(p=>({...p, nuevo:!p.nuevo}))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPwds.nuevo ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Confirmar contraseña *</label>
                <input type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pwd.confirmar} onChange={e=>setPwd(p=>({...p, confirmar:e.target.value}))} required/>
              </div>
              <div className="pt-2 flex justify-end">
                <button type="submit" disabled={pwdMutation.isPending}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 shadow-sm">
                  {pwdMutation.isPending ? "Actualizando..." : "Actualizar contraseña"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Accesos jugadores/padres */}
        {tab === "usuarios" && isAdmin() && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="font-semibold text-gray-900">Accesos jugadores y padres</h2>
                <p className="text-xs text-gray-500 mt-0.5">Pueden ver información pero no modificar datos</p>
              </div>
              <button onClick={() => setShowUForm(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg text-sm font-medium">
                <Plus size={14}/> Crear acceso
              </button>
            </div>

            {showUForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                  <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <h3 className="font-bold text-gray-900">Crear acceso</h3>
                    <button onClick={() => setShowUForm(false)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"><X size={18}/></button>
                  </div>
                  <form onSubmit={e=>{e.preventDefault(); crearU.mutate(uForm);}} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Nombre *</label>
                      <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={uForm.nombre} onChange={e=>setUForm(f=>({...f, nombre:e.target.value}))} required/>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Apellido</label>
                      <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={uForm.apellido} onChange={e=>setUForm(f=>({...f, apellido:e.target.value}))}/>
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Email *</label>
                      <input type="email" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={uForm.email} onChange={e=>setUForm(f=>({...f, email:e.target.value}))} required/>
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Contraseña *</label>
                      <div className="relative">
                        <input type={showUPwd?"text":"password"} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={uForm.password} onChange={e=>setUForm(f=>({...f, password:e.target.value}))} required/>
                        <button type="button" onClick={()=>setShowUPwd(!showUPwd)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium">
                          {showUPwd?"Ocultar":"Mostrar"}
                        </button>
                      </div>
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Tipo de acceso *</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={uForm.rol} onChange={e=>setUForm(f=>({...f, rol:e.target.value}))}>
                        <option value="jugador">Jugador</option>
                        <option value="padre">Padre / Tutor</option>
                      </select>
                    </div>
                    <div className="col-span-1 sm:col-span-2 flex gap-3 justify-end pt-2">
                      <button type="button" onClick={() => setShowUForm(false)}
                        className="px-5 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancelar</button>
                      <button type="submit" disabled={crearU.isPending}
                        className="px-5 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm">
                        {crearU.isPending ? "Creando..." : "Crear acceso"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {loadingU ? (
              <div className="p-6">
                {[1,2,3].map(i=>(
                  <div key={i} className="animate-pulse flex items-center gap-4 py-3">
                    <div className="w-9 h-9 rounded-full bg-gray-200"/>
                    <div className="flex-1"><div className="h-3 bg-gray-200 rounded w-1/3 mb-2"/><div className="h-2.5 bg-gray-100 rounded w-1/4"/></div>
                  </div>
                ))}
              </div>
            ) : (usuarios as any[]).length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Users size={32} className="mx-auto mb-2 text-gray-300"/>
                <p className="text-sm">Sin accesos de jugadores o padres</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {(usuarios as any[]).map(u => (
                  <div key={u.id} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">
                      {((u.nombre?.[0]||"")+(u.apellido?.[0]||"")).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{u.nombre} {u.apellido}</div>
                      <div className="text-xs text-gray-500 truncate">{u.email}</div>
                    </div>
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-gray-100 text-gray-600">
                      {ROL_LABELS[u.rol] || u.rol}
                    </span>
                    <button
                      onClick={() => toggleU.mutate({ id: u.id, activo: !u.is_active })}
                      className="flex items-center gap-1.5 text-xs font-medium"
                      title={u.is_active ? "Desactivar" : "Activar"}
                    >
                      {u.is_active
                        ? <><ToggleRight size={20} className="text-green-500"/><span className="text-green-600 hidden sm:inline">Activo</span></>
                        : <><ToggleLeft size={20} className="text-gray-400"/><span className="text-gray-400 hidden sm:inline">Inactivo</span></>
                      }
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
